/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { billingApi, clientsApi, mattersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import HelpButton from "@/components/HelpButton";
import {
    Receipt, Clock, Wallet, Files, ArrowRight, Plus, Play, Stop, TrashSimple,
    Coins, Briefcase, CurrencyInr, WarningOctagon, CheckCircle, ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtINR(n) {
    if (n == null) return "—";
    const x = Number(n);
    return "₹ " + x.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMinutes(m) {
    if (!m) return "0h";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm ? `${h}h ${mm}m` : `${h}h`;
}

export default function Billing() {
    const [tab, setTab] = useState("overview");
    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="billing-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Practice</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Billing & Finance</h1>
                    <HelpButton topic="billing" testid="help-billing" />
                </div>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="overview" data-testid="tab-overview"><ChartLineUp size={14} className="mr-1" /> Overview</TabsTrigger>
                    <TabsTrigger value="time" data-testid="tab-time"><Clock size={14} className="mr-1" /> Time</TabsTrigger>
                    <TabsTrigger value="expenses" data-testid="tab-expenses"><Wallet size={14} className="mr-1" /> Expenses</TabsTrigger>
                    <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt size={14} className="mr-1" /> Invoices</TabsTrigger>
                    <TabsTrigger value="agreements" data-testid="tab-agreements"><Files size={14} className="mr-1" /> Agreements</TabsTrigger>
                </TabsList>
                <TabsContent value="overview"><OverviewTab /></TabsContent>
                <TabsContent value="time"><TimeTab /></TabsContent>
                <TabsContent value="expenses"><ExpensesTab /></TabsContent>
                <TabsContent value="invoices"><InvoicesTab /></TabsContent>
                <TabsContent value="agreements"><AgreementsTab /></TabsContent>
            </Tabs>
        </div>
    );
}

// =================== Overview ===================

function OverviewTab() {
    const [stats, setStats] = useState(null);
    const [outstanding, setOutstanding] = useState([]);
    useEffect(() => {
        billingApi.dashboard().then(setStats).catch(() => {});
        billingApi.outstanding().then(setOutstanding).catch(() => {});
    }, []);
    if (!stats) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-6 pt-4" data-testid="overview-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Invoiced this FY" value={fmtINR(stats.invoiced_this_fy)} sub={`Since ${stats.fy_start}`} icon={Receipt} />
                <StatCard label="Collected (all-time)" value={fmtINR(stats.collected_total)} icon={CheckCircle} tone="emerald" />
                <StatCard label="Outstanding" value={fmtINR(stats.outstanding_total)} sub={`${stats.sent_invoice_count} live invoices`} icon={CurrencyInr} tone={stats.outstanding_total > 0 ? "amber" : "default"} />
                <StatCard label="Overdue" value={fmtINR(stats.overdue_total)} icon={WarningOctagon} tone={stats.overdue_total > 0 ? "destructive" : "default"} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatCard label="WIP — Unbilled time" value={fmtINR(stats.wip_time_amount)} sub={`${stats.wip_time_count} entries`} icon={Clock} />
                <StatCard label="WIP — Unbilled expenses" value={fmtINR(stats.wip_expense_amount)} sub={`${stats.wip_expense_count} items`} icon={Wallet} />
                <StatCard label="Expenses this FY" value={fmtINR(stats.expenses_this_fy)} icon={Coins} />
            </div>

            <section data-testid="outstanding-section">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="font-serif text-2xl">Outstanding invoices</h2>
                    <span className="text-sm text-muted-foreground">{outstanding.length} pending</span>
                </div>
                {!outstanding.length ? (
                    <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">
                        Nothing outstanding — you're fully collected.
                    </div>
                ) : (
                    <div className="border border-border bg-white rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-muted-foreground">
                                <tr>
                                    <th className="text-left p-2">Invoice</th>
                                    <th className="text-left p-2">Client</th>
                                    <th className="text-left p-2">Due</th>
                                    <th className="text-right p-2">Amount due</th>
                                    <th className="text-left p-2">Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {outstanding.map((i) => (
                                    <tr key={i.id} className="border-t border-border hover:bg-muted/30" data-testid={`outstanding-${i.id}`}>
                                        <td className="p-2 font-mono text-xs">{i.invoice_number}</td>
                                        <td className="p-2">{i.client_snapshot?.name}</td>
                                        <td className="p-2 text-xs">{i.due_date}</td>
                                        <td className="p-2 text-right font-medium">{fmtINR(i.amount_due)}</td>
                                        <td className="p-2"><span className={`chip ${i.is_overdue ? "chip-doc text-destructive" : "chip-ai"}`}>{i.is_overdue ? "overdue" : i.status}</span></td>
                                        <td className="p-2"><Link to={`/billing/invoices/${i.id}`} className="text-muted-foreground hover:text-foreground"><ArrowRight size={14} /></Link></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

function StatCard({ label, value, sub, icon: Icon, tone = "default" }) {
    const ring = {
        emerald: "border-emerald-300 bg-emerald-50/40",
        amber: "border-amber-300 bg-amber-50/40",
        destructive: "border-destructive/40 bg-destructive/5",
        default: "border-border bg-white",
    }[tone];
    return (
        <div className={`border rounded-md p-3 ${ring}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
                <Icon size={11} /> {label}
            </div>
            <div className="font-serif text-2xl mt-1">{value}</div>
            {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        </div>
    );
}

// =================== Time ===================

function TimeTab() {
    const [entries, setEntries] = useState([]);
    const [running, setRunning] = useState(null);
    const [matters, setMatters] = useState([]);
    const [matterFilter, setMatterFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [tick, setTick] = useState(0);
    const load = async () => {
        const p = matterFilter === "all" ? {} : { matter_id: matterFilter };
        setEntries(await billingApi.listTime(p).catch(() => []));
        setRunning(await billingApi.runningTimer().catch(() => null));
    };
    useEffect(() => { load(); }, [matterFilter]);
    useEffect(() => { mattersApi.list().then(setMatters).catch(() => {}); }, []);
    useEffect(() => {
        if (!running) return;
        const t = setInterval(() => setTick((x) => x + 1), 1000);
        return () => clearInterval(t);
    }, [running]);

    const startTimer = async (matter_id, description) => {
        try {
            const e = await billingApi.startTimer(matter_id, description);
            setRunning(e);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to start timer");
        }
    };
    const stopTimer = async () => {
        if (!running) return;
        try {
            await billingApi.stopTimer(running.id);
            setRunning(null);
            load();
            toast.success("Timer stopped");
        } catch {
            toast.error("Failed to stop timer");
        }
    };
    const remove = async (id) => {
        try { await billingApi.removeTime(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const elapsed = running ? Math.max(0, Math.floor((Date.now() - new Date(running.started_at).getTime()) / 1000)) : 0;
    const elapsedFmt = `${Math.floor(elapsed / 3600)}:${String(Math.floor(elapsed / 60) % 60).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;

    return (
        <div className="space-y-4 pt-4" data-testid="time-tab">
            {running ? (
                <div className="border-2 border-emerald-400 bg-emerald-50/50 rounded-md p-4 flex items-center gap-4" data-testid="timer-running">
                    <div className="h-3 w-3 rounded-full bg-emerald-500 animate-pulse" />
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Timer running</div>
                        <div className="font-serif text-xl">{running.description || "(no description)"}</div>
                        <div className="text-xs text-muted-foreground">on matter {running.matter_id?.slice(0, 8)}</div>
                    </div>
                    <div className="font-mono text-3xl tracking-tight">{elapsedFmt}</div>
                    <Button variant="outline" size="sm" onClick={stopTimer} data-testid="stop-timer-btn"><Stop size={14} weight="fill" /> Stop</Button>
                </div>
            ) : (
                <div className="border border-dashed border-border rounded-md p-4">
                    <StartTimerInline matters={matters} onStart={startTimer} />
                </div>
            )}

            <div className="flex justify-between items-center gap-3 flex-wrap">
                <Select value={matterFilter} onValueChange={setMatterFilter}>
                    <SelectTrigger className="w-[260px]" data-testid="time-matter-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All matters</SelectItem>
                        {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="manual-time-btn"><Plus size={13} /> Manual entry</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Manual time entry</DialogTitle>
                            <DialogDescription>Use this for work logged after the fact (e.g., a hearing where you couldn't start a timer).</DialogDescription>
                        </DialogHeader>
                        <ManualTimeForm matters={matters} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>

            {!entries.length ? (
                <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">
                    No time entries yet.
                </div>
            ) : (
                <div className="border border-border bg-white rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Description</th>
                                <th className="text-left p-2 hidden md:table-cell">Matter</th>
                                <th className="text-right p-2">Duration</th>
                                <th className="text-right p-2 hidden lg:table-cell">Rate</th>
                                <th className="text-right p-2">Amount</th>
                                <th className="text-left p-2">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((e) => {
                                const m = matters.find((x) => x.id === e.matter_id);
                                const amount = (e.minutes / 60) * (e.hourly_rate || 0);
                                return (
                                    <tr key={e.id} className="border-t border-border hover:bg-muted/30" data-testid={`time-row-${e.id}`}>
                                        <td className="p-2 text-xs">{(e.started_at || "").slice(0, 10)}</td>
                                        <td className="p-2">{e.description}</td>
                                        <td className="p-2 hidden md:table-cell text-xs text-muted-foreground">{m?.title || "—"}</td>
                                        <td className="p-2 text-right">{fmtMinutes(e.minutes)}</td>
                                        <td className="p-2 text-right hidden lg:table-cell">{fmtINR(e.hourly_rate)}</td>
                                        <td className="p-2 text-right font-medium">{fmtINR(amount)}</td>
                                        <td className="p-2"><span className={`chip ${e.status === "running" ? "chip-ai" : e.invoice_id ? "chip-doc" : "chip-ai"}`}>{e.invoice_id ? "billed" : e.status}</span></td>
                                        <td className="p-2">{!e.invoice_id && (
                                            <button onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete" data-testid={`time-delete-${e.id}`}>
                                                <TrashSimple size={14} />
                                            </button>
                                        )}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StartTimerInline({ matters, onStart }) {
    const [v, setV] = useState({ matter_id: "", description: "" });
    return (
        <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[180px]">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Matter</div>
                <Select value={v.matter_id} onValueChange={(val) => setV({ ...v, matter_id: val })}>
                    <SelectTrigger data-testid="start-timer-matter"><SelectValue placeholder="Pick a matter…" /></SelectTrigger>
                    <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div className="flex-1 min-w-[220px]">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">What are you working on?</div>
                <Input value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} placeholder="e.g., Drafting reply" data-testid="start-timer-desc" />
            </div>
            <Button onClick={() => v.matter_id ? onStart(v.matter_id, v.description) : toast.error("Pick a matter")} disabled={!v.matter_id} data-testid="start-timer-btn">
                <Play size={13} weight="fill" /> Start timer
            </Button>
        </div>
    );
}

function ManualTimeForm({ matters, onDone }) {
    const [v, setV] = useState({ matter_id: "", description: "", minutes: 60, started_at: new Date().toISOString().slice(0, 16), billable: true });
    const submit = async () => {
        if (!v.matter_id) { toast.error("Matter required"); return; }
        try {
            await billingApi.createTime({ ...v, minutes: parseInt(v.minutes, 10), started_at: v.started_at + ":00" });
            toast.success("Entry added");
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };
    return (
        <div className="space-y-3" data-testid="manual-time-form">
            <Select value={v.matter_id} onValueChange={(val) => setV({ ...v, matter_id: val })}>
                <SelectTrigger data-testid="mte-matter"><SelectValue placeholder="Matter…" /></SelectTrigger>
                <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="What did you do?" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="mte-desc" />
            <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="Minutes" value={v.minutes} onChange={(e) => setV({ ...v, minutes: e.target.value })} data-testid="mte-mins" />
                <Input type="datetime-local" value={v.started_at} onChange={(e) => setV({ ...v, started_at: e.target.value })} data-testid="mte-when" />
            </div>
            <div className="flex justify-end">
                <Button onClick={submit} data-testid="mte-submit">Add entry</Button>
            </div>
        </div>
    );
}

// =================== Expenses ===================

function ExpensesTab() {
    const [expenses, setExpenses] = useState([]);
    const [matters, setMatters] = useState([]);
    const [cats, setCats] = useState([]);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all");
    const load = async () => setExpenses(await billingApi.listExpenses(filter === "unbilled" ? { unbilled_only: true } : {}).catch(() => []));
    useEffect(() => { load(); }, [filter]);
    useEffect(() => {
        mattersApi.list().then(setMatters).catch(() => {});
        billingApi.expenseCategories().then(setCats).catch(() => {});
    }, []);
    const remove = async (id) => { try { await billingApi.removeExpense(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

    return (
        <div className="space-y-4 pt-4" data-testid="expenses-tab">
            <div className="flex justify-between gap-3 flex-wrap">
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="expense-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All expenses</SelectItem>
                        <SelectItem value="unbilled">Unbilled only</SelectItem>
                    </SelectContent>
                </Select>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" data-testid="new-expense-btn"><Plus size={13} /> Log expense</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Log expense</DialogTitle></DialogHeader>
                        <ExpenseForm matters={matters} categories={cats} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!expenses.length ? (
                <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">No expenses.</div>
            ) : (
                <div className="border border-border bg-white rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Description</th>
                                <th className="text-left p-2 hidden md:table-cell">Category</th>
                                <th className="text-left p-2 hidden md:table-cell">Matter</th>
                                <th className="text-right p-2">Amount</th>
                                <th className="text-left p-2">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map((e) => {
                                const m = matters.find((x) => x.id === e.matter_id);
                                return (
                                    <tr key={e.id} className="border-t border-border hover:bg-muted/30" data-testid={`expense-row-${e.id}`}>
                                        <td className="p-2 text-xs">{e.expense_date}</td>
                                        <td className="p-2">{e.description}</td>
                                        <td className="p-2 hidden md:table-cell"><span className="chip chip-doc text-xs">{e.category}</span></td>
                                        <td className="p-2 hidden md:table-cell text-xs text-muted-foreground">{m?.title || "—"}</td>
                                        <td className="p-2 text-right font-medium">{fmtINR(e.amount)}</td>
                                        <td className="p-2"><span className={`chip ${e.invoice_id ? "chip-doc" : "chip-ai"}`}>{e.invoice_id ? "billed" : "unbilled"}</span></td>
                                        <td className="p-2">{!e.invoice_id && (
                                            <button onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive" data-testid={`expense-delete-${e.id}`} aria-label="Delete">
                                                <TrashSimple size={14} />
                                            </button>
                                        )}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ExpenseForm({ matters, categories, onDone }) {
    const [v, setV] = useState({ matter_id: "", description: "", amount: "", category: "miscellaneous", expense_date: new Date().toISOString().slice(0, 10) });
    const submit = async () => {
        if (!v.matter_id || !v.amount || !v.description) { toast.error("Matter, description, amount required"); return; }
        try {
            await billingApi.createExpense({ ...v, amount: parseFloat(v.amount) });
            toast.success("Expense logged");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="expense-form">
            <Select value={v.matter_id} onValueChange={(val) => setV({ ...v, matter_id: val })}>
                <SelectTrigger data-testid="ef-matter"><SelectValue placeholder="Matter…" /></SelectTrigger>
                <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Description" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="ef-desc" />
            <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="Amount (₹)" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} data-testid="ef-amount" />
                <Input type="date" value={v.expense_date} onChange={(e) => setV({ ...v, expense_date: e.target.value })} data-testid="ef-date" />
            </div>
            <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}>
                <SelectTrigger data-testid="ef-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex justify-end"><Button onClick={submit} data-testid="ef-submit">Log expense</Button></div>
        </div>
    );
}

// =================== Invoices ===================

function InvoicesTab() {
    const [invoices, setInvoices] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setInvoices(await billingApi.listInvoices().catch(() => []));
    useEffect(() => { load(); }, []);
    return (
        <div className="space-y-4 pt-4" data-testid="invoices-tab">
            <div className="flex justify-between gap-2">
                <div className="text-sm text-muted-foreground">{invoices.length} invoice(s)</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button data-testid="new-invoice-btn"><Plus size={14} /> Generate invoice</Button></DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Generate invoice</DialogTitle>
                            <DialogDescription>Pulls all unbilled time + expenses for the chosen client (or matter) and creates a GST-compliant tax invoice.</DialogDescription>
                        </DialogHeader>
                        <GenerateInvoiceForm onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!invoices.length ? (
                <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">No invoices yet.</div>
            ) : (
                <div className="border border-border bg-white rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">Invoice</th>
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2 hidden md:table-cell">Client</th>
                                <th className="text-right p-2">Subtotal</th>
                                <th className="text-right p-2">Net due</th>
                                <th className="text-right p-2">Paid</th>
                                <th className="text-left p-2">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((i) => (
                                <tr key={i.id} className="border-t border-border hover:bg-muted/30" data-testid={`invoice-row-${i.id}`}>
                                    <td className="p-2 font-mono text-xs">{i.invoice_number}</td>
                                    <td className="p-2 text-xs">{i.invoice_date}</td>
                                    <td className="p-2 hidden md:table-cell">{i.client_snapshot?.name}</td>
                                    <td className="p-2 text-right">{fmtINR(i.subtotal)}</td>
                                    <td className="p-2 text-right font-medium">{fmtINR(i.net_due)}</td>
                                    <td className="p-2 text-right">{fmtINR(i.paid_amount)}</td>
                                    <td className="p-2"><InvoiceStatusChip i={i} /></td>
                                    <td className="p-2"><Link to={`/billing/invoices/${i.id}`} className="text-muted-foreground hover:text-foreground"><ArrowRight size={14} /></Link></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function InvoiceStatusChip({ i }) {
    const cls = {
        paid: "chip-ai text-emerald-700",
        partially_paid: "chip-ai",
        draft: "chip-doc",
        sent: "chip-doc",
        overdue: "chip-doc text-destructive",
        cancelled: "chip-doc",
    }[i.status] || "chip-doc";
    return <span className={`chip ${cls}`}>{i.status}</span>;
}

function GenerateInvoiceForm({ onDone }) {
    const [clients, setClients] = useState([]);
    const [matters, setMatters] = useState([]);
    const [v, setV] = useState({
        client_id: "", matter_id: "all",
        gst_mode: "standard", gst_rate: 18, tds_rate: 10,
        sac_code: "998212", place_of_supply: "Delhi",
        advocate_gstin: "", client_gstin: "",
        include_time: true, include_expenses: true,
        fixed_line: "", fixed_description: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        clientsApi.list().then(setClients).catch(() => {});
        mattersApi.list().then(setMatters).catch(() => {});
    }, []);
    const clientMatters = useMemo(() => matters.filter((m) => m.client_id === v.client_id), [matters, v.client_id]);
    const submit = async () => {
        if (!v.client_id) { toast.error("Pick a client"); return; }
        setBusy(true);
        try {
            const payload = {
                ...v,
                matter_id: v.matter_id === "all" ? null : v.matter_id,
                fixed_line: v.fixed_line ? parseFloat(v.fixed_line) : null,
                fixed_description: v.fixed_description || null,
            };
            const inv = await billingApi.generateInvoice(payload);
            toast.success(`${inv.invoice_number} generated · Net due ${fmtINR(inv.net_due)}`);
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to generate");
        }
        setBusy(false);
    };
    return (
        <div className="space-y-3" data-testid="generate-invoice-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Client</div>
                    <Select value={v.client_id} onValueChange={(val) => setV({ ...v, client_id: val, matter_id: "all" })}>
                        <SelectTrigger data-testid="gi-client"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                        <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Matter scope</div>
                    <Select value={v.matter_id} onValueChange={(val) => setV({ ...v, matter_id: val })}>
                        <SelectTrigger data-testid="gi-matter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All matters for this client</SelectItem>
                            {clientMatters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">GST mode</div>
                    <Select value={v.gst_mode} onValueChange={(g) => setV({ ...v, gst_mode: g })}>
                        <SelectTrigger data-testid="gi-gst-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="standard">Standard (charge GST)</SelectItem>
                            <SelectItem value="rcm">RCM (no GST, recipient pays)</SelectItem>
                            <SelectItem value="exempt">Exempt</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Field label="GST rate (%)"><Input type="number" value={v.gst_rate} onChange={(e) => setV({ ...v, gst_rate: parseFloat(e.target.value || "0") })} disabled={v.gst_mode !== "standard"} /></Field>
                <Field label="TDS rate (%)"><Input type="number" value={v.tds_rate} onChange={(e) => setV({ ...v, tds_rate: parseFloat(e.target.value || "0") })} /></Field>
                <Field label="SAC code"><Input value={v.sac_code} onChange={(e) => setV({ ...v, sac_code: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Advocate GSTIN"><Input value={v.advocate_gstin} onChange={(e) => setV({ ...v, advocate_gstin: e.target.value })} placeholder="07AABCD1234E1Z9" /></Field>
                <Field label="Client GSTIN"><Input value={v.client_gstin} onChange={(e) => setV({ ...v, client_gstin: e.target.value })} placeholder="optional" /></Field>
            </div>
            <Field label="Add fixed-fee line (optional)">
                <div className="grid grid-cols-3 gap-2">
                    <Input type="number" placeholder="Amount" value={v.fixed_line} onChange={(e) => setV({ ...v, fixed_line: e.target.value })} />
                    <Input className="col-span-2" placeholder="Description (e.g., 'Fixed professional fees for ABC matter')" value={v.fixed_description} onChange={(e) => setV({ ...v, fixed_description: e.target.value })} />
                </div>
            </Field>
            <Field label="Notes"><Textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></Field>
            <div className="flex items-center gap-3 text-sm">
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={v.include_time} onChange={(e) => setV({ ...v, include_time: e.target.checked })} className="accent-emerald-600" /> Include time</label>
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={v.include_expenses} onChange={(e) => setV({ ...v, include_expenses: e.target.checked })} className="accent-emerald-600" /> Include expenses</label>
            </div>
            <div className="flex justify-end pt-1">
                <Button onClick={submit} disabled={busy || !v.client_id} data-testid="gi-submit">{busy ? "Generating…" : "Generate"}</Button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>{children}</div>;
}

// =================== Agreements ===================

function AgreementsTab() {
    const [arr, setArr] = useState([]);
    const [clients, setClients] = useState([]);
    const [matters, setMatters] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setArr(await billingApi.listAgreements().catch(() => []));
    useEffect(() => {
        load();
        clientsApi.list().then(setClients).catch(() => {});
        mattersApi.list().then(setMatters).catch(() => {});
    }, []);
    const remove = async (id) => { try { await billingApi.removeAgreement(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
    return (
        <div className="space-y-4 pt-4" data-testid="agreements-tab">
            <div className="flex justify-end">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button data-testid="new-agreement-btn"><Plus size={14} /> New agreement</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New fee agreement</DialogTitle>
                            <DialogDescription>This rate auto-applies to time entries on the client/matter.</DialogDescription>
                        </DialogHeader>
                        <AgreementForm clients={clients} matters={matters} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!arr.length ? (
                <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">No fee agreements yet.</div>
            ) : (
                <div className="border border-border bg-white rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">Client</th>
                                <th className="text-left p-2 hidden md:table-cell">Matter</th>
                                <th className="text-left p-2">Kind</th>
                                <th className="text-right p-2">Rate / Fee</th>
                                <th className="text-left p-2">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {arr.map((a) => {
                                const c = clients.find((x) => x.id === a.client_id);
                                const m = matters.find((x) => x.id === a.matter_id);
                                return (
                                    <tr key={a.id} className="border-t border-border hover:bg-muted/30" data-testid={`agreement-row-${a.id}`}>
                                        <td className="p-2">{c?.name || a.client_id?.slice(0, 8)}</td>
                                        <td className="p-2 hidden md:table-cell text-xs text-muted-foreground">{m?.title || (a.matter_id ? "—" : "All matters")}</td>
                                        <td className="p-2"><span className="chip chip-doc">{a.kind}</span></td>
                                        <td className="p-2 text-right">
                                            {a.kind === "hourly" && fmtINR(a.hourly_rate) + " / hr"}
                                            {a.kind === "fixed" && fmtINR(a.fixed_fee)}
                                            {a.kind === "retainer" && fmtINR(a.retainer_amount) + " (bal " + fmtINR(a.retainer_balance) + ")"}
                                            {a.kind === "contingency" && "—"}
                                        </td>
                                        <td className="p-2"><span className={`chip ${a.status === "active" ? "chip-ai" : "chip-doc"}`}>{a.status}</span></td>
                                        <td className="p-2">
                                            <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`agreement-delete-${a.id}`} aria-label="Delete">
                                                <TrashSimple size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AgreementForm({ clients, matters, onDone }) {
    const [v, setV] = useState({ client_id: "", matter_id: "all", kind: "hourly", hourly_rate: "", fixed_fee: "", retainer_amount: "", terms: "" });
    const clientMatters = useMemo(() => matters.filter((m) => m.client_id === v.client_id), [matters, v.client_id]);
    const submit = async () => {
        if (!v.client_id) { toast.error("Pick a client"); return; }
        try {
            const payload = {
                client_id: v.client_id,
                matter_id: v.matter_id === "all" ? null : v.matter_id,
                kind: v.kind,
                terms: v.terms,
                hourly_rate: v.hourly_rate ? parseFloat(v.hourly_rate) : null,
                fixed_fee: v.fixed_fee ? parseFloat(v.fixed_fee) : null,
                retainer_amount: v.retainer_amount ? parseFloat(v.retainer_amount) : null,
            };
            await billingApi.createAgreement(payload);
            toast.success("Agreement created");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="agreement-form">
            <Select value={v.client_id} onValueChange={(val) => setV({ ...v, client_id: val, matter_id: "all" })}>
                <SelectTrigger data-testid="af-client"><SelectValue placeholder="Client…" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={v.matter_id} onValueChange={(val) => setV({ ...v, matter_id: val })}>
                <SelectTrigger data-testid="af-matter"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All matters (client-wide)</SelectItem>
                    {clientMatters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={v.kind} onValueChange={(k) => setV({ ...v, kind: k })}>
                <SelectTrigger data-testid="af-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="fixed">Fixed fee</SelectItem>
                    <SelectItem value="retainer">Retainer</SelectItem>
                    <SelectItem value="contingency">Contingency</SelectItem>
                </SelectContent>
            </Select>
            {v.kind === "hourly" && <Input type="number" placeholder="Hourly rate (₹)" value={v.hourly_rate} onChange={(e) => setV({ ...v, hourly_rate: e.target.value })} data-testid="af-hourly" />}
            {v.kind === "fixed" && <Input type="number" placeholder="Fixed fee (₹)" value={v.fixed_fee} onChange={(e) => setV({ ...v, fixed_fee: e.target.value })} data-testid="af-fixed" />}
            {v.kind === "retainer" && <Input type="number" placeholder="Retainer amount (₹)" value={v.retainer_amount} onChange={(e) => setV({ ...v, retainer_amount: e.target.value })} data-testid="af-retainer" />}
            <Textarea rows={2} placeholder="Terms" value={v.terms} onChange={(e) => setV({ ...v, terms: e.target.value })} data-testid="af-terms" />
            <div className="flex justify-end"><Button onClick={submit} data-testid="af-submit">Create</Button></div>
        </div>
    );
}
